import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Download, MessageCircle, Plus, Trash2, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import { deleteSavedList, getSavedListById } from '../../services/savedListService';
import { getSensitiveData, saveSensitiveData } from '../../utils/secureStorage';
import type { SavedListDto } from '../../types/savedLists';
import CreateGroupModal from '../../components/groups/CreateGroupModal';
import Toast from '../../components/Toast/Toast';
import { AvatarImage } from '../../components/common/AvatarImage';
import { useMenuLabels } from '@/hooks/useMenuLabels';

const EXPORT_FIELDS = [
  'Name',
  'Email',
  'IT Specialty',
  'Experience',
  'Availability',
  'Current Organization',
  'Skills',
];

export default function SavedListDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [savedList, setSavedList] = useState<SavedListDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const labels = useMenuLabels();
  const listLabel = labels.list;

  useEffect(() => {
    let isCurrent = true;

    const load = async () => {
      setIsLoading(true);
      try {
        const userId = (await getSensitiveData('userId')) || '';
        if (!isCurrent) return;

        setCurrentUserId(userId);
        if (!id) {
          setSavedList(null);
          setSelectedIds([]);
          return;
        }

        const list = await getSavedListById(id);
        if (!isCurrent) return;

        setSavedList(list || null);
        setSelectedIds(list?.memberUserIds ?? []);
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isCurrent = false;
    };
  }, [id]);

  const members = savedList?.members ?? [];
  const selectedCount = selectedIds.length;
  const allSelected = savedList ? selectedIds.length === savedList.memberUserIds.length && selectedIds.length > 0 : false;

  const setSelectedListIds = (updated: string[]) => {
    setSelectedIds(updated);
  };

  const handleToggleSelect = (userId: string) => {
    if (selectedIds.includes(userId)) {
      setSelectedListIds(selectedIds.filter((idToRemove) => idToRemove !== userId));
    } else {
      setSelectedListIds([...selectedIds, userId]);
    }
  };

  const handleToggleAll = () => {
    if (!savedList) {
      return;
    }

    if (allSelected) {
      setSelectedListIds([]);
    } else {
      setSelectedListIds([...savedList.memberUserIds]);
    }
  };

  const buildMemberLabel = (member: any) => {
    return member.firstName || member.fullName || member.userName || 'Unknown';
  };

  const getAvailabilityLabel = (member: any) => {
    const availability = member.availabilities?.[0]?.availabilityTypeName || member.availability || 'Unknown';
    const hours = member.availabilities?.[0]?.availableHoursPerWeek;
    return hours ? `${availability} • ${hours} hrs/week` : availability;
  };

  const downloadCsv = () => {
    if (!savedList) {
      return;
    }
    const rows = savedList.members.map((member) => ({
      Name: buildMemberLabel(member),
      Email: member.userName || '',
      'IT Specialty': member.itSpecialtyName || '',
      Experience: member.totalExperienceInYears?.toString() || '',
      Availability: getAvailabilityLabel(member),
      'Current Organization': member.currentOrganization || '',
      Skills: Array.isArray(member.skills) ? member.skills.map((skill) => skill.skillName).filter(Boolean).join(', ') : '',
    }));

    const header = EXPORT_FIELDS.join(',');
    const csvContent = [header, ...rows.map((row) => EXPORT_FIELDS.map((field) => `"${String(row[field as keyof typeof row] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${savedList.listName.replace(/\s+/g, '_') || 'saved-list'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setToast({ message: `${listLabel} exported successfully.`, type: 'success' });
  };

  const downloadExcel = () => {
    if (!savedList) {
      return;
    }

    const rows = savedList.members.map((member) => ({
      Name: buildMemberLabel(member),
      Email: member.userName || '',
      'IT Specialty': member.itSpecialtyName || '',
      Experience: member.totalExperienceInYears?.toString() || '',
      Availability: getAvailabilityLabel(member),
      'Current Organization': member.currentOrganization || '',
      Skills: Array.isArray(member.skills) ? member.skills.map((skill) => skill.skillName).filter(Boolean).join(', ') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: EXPORT_FIELDS });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Private List');
    const workbookBlob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], {
      type: 'application/octet-stream',
    });

    const url = URL.createObjectURL(workbookBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${savedList.listName.replace(/\s+/g, '_') || 'saved-list'}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
    setToast({ message: `${listLabel} exported successfully.`, type: 'success' });
  };

  const handleMessageAll = () => {
    if (!savedList?.members.length) {
      setToast({ message: 'No members available to message.', type: 'warning' });
      return;
    }
    
    const encoded = encodeURIComponent(JSON.stringify(savedList.members));
    navigate(`/send-message?userIds=${encoded}`);
  };

  const handleMessageSelected = () => {
    if (!selectedIds.length) {
      setToast({ message: 'Please select at least one member.', type: 'warning' });
      return;
    }
    
    const encoded = encodeURIComponent(JSON.stringify(selectedUsers));
    navigate(`/send-message?userIds=${encoded}`);
  };

  const handleCreateGroup = () => {
    if (!selectedIds.length) {
      setToast({ message: `Select at least one member to create a ${listLabel.toLowerCase()}.`, type: 'warning' });
      return;
    }
    setIsGroupOpen(true);
  };

  const handleDelete = async () => {
    if (!savedList?.id) {
      return;
    }
    try {
      await deleteSavedList(savedList.id);
      setToast({ message: `${listLabel} deleted successfully.`, type: 'success' });
      navigate('/saved-lists');
    } catch (error) {
      setShowDeleteConfirm(false);
      setToast({ message: `Failed to delete ${listLabel.toLowerCase()}. Please try again.`, type: 'error' });
    }
  };

  const handleViewProfile = async (member: any) => {
    await saveSensitiveData('selectedMatchId', String(member.userId));
    await saveSensitiveData('selectedMatchProfile', JSON.stringify(member));
    navigate('/MemberProfile');
  };

  const selectedUsers = useMemo(
    () => members.filter((member) => selectedIds.includes(member.userId)),
    [members, selectedIds]
  );

  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
        Loading {labels.privateList}...
      </div>
    );
  }

  if (!savedList) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <p className="text-xl font-semibold text-gray-900">{labels.privateList} not found</p>
        <p className="mt-3 text-sm text-gray-600">The selected list could not be loaded. It may have been deleted.</p>
        <button
          onClick={() => navigate('/saved-lists')}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          Back to {labels.list}s
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/saved-lists')}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <h1 className="min-w-0 flex-1 break-words text-3xl font-bold text-gray-900">{savedList.listName}</h1>
          <div className="grid shrink-0 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">{savedList.memberCount ?? savedList.memberUserIds.length} Members</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">Created By</p>
              <p>{savedList.createdByUserId === currentUserId ? 'You' : savedList.createdByName || savedList.createdByUserId}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">Created On</p>
              <p>{new Date(savedList.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
        {savedList.description ? <p className="mt-4 whitespace-pre-line break-words text-sm text-gray-600">{savedList.description}</p> : null}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={handleMessageAll}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <MessageCircle className="h-4 w-4" />
            Message All
          </button>
          <button
            onClick={handleMessageSelected}
            // disabled={!selectedCount}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
          >
            <MessageCircle className="h-4 w-4" />
            Message Selected
          </button>
          <button
            onClick={handleCreateGroup}
            // disabled={!selectedCount}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
          >
            <Users className="h-4 w-4" />
            Create {listLabel}
          </button>
          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={downloadExcel}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export Excel
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100"
          >
            <Trash2 className="h-4 w-4" />
            Delete {listLabel}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Members</h2>
            <p className="text-sm text-gray-600">Select individuals to message or include in a new list.</p>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <button
              type="button"
              onClick={handleToggleAll}
              className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 text-sm font-medium transition hover:bg-slate-100"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
            <span>{selectedCount} selected</span>
          </div>
        </div>

        {/* Mobile: card layout (below md) */}
        <div className="flex flex-col gap-3 md:hidden">
          {members.map((member) => {
            const selected = selectedIds.includes(member.userId);
            return (
              <div key={member.userId} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    aria-label={`${selected ? 'Deselect' : 'Select'} ${buildMemberLabel(member)}`}
                    aria-pressed={selected}
                    onClick={() => handleToggleSelect(member.userId)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 transition hover:bg-gray-100"
                  >
                    {selected ? <Check className="h-4 w-4 text-blue-600" /> : null}
                  </button>
                  <AvatarImage
                    profilePhotoId={Number(member.profilePhotoId ?? member.photoId) || null}
                    name={buildMemberLabel(member)}
                    alt={buildMemberLabel(member)}
                    size={40}
                    className="shrink-0 rounded-xl"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-gray-900">{buildMemberLabel(member)}</div>
                    <button
                      type="button"
                      onClick={() => handleViewProfile(member)}
                      className="mt-0.5 text-xs font-medium text-blue-600 hover:underline"
                    >
                      View Profile
                    </button>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-gray-600">
                  <div>
                    <dt className="text-xs font-medium text-slate-500">IT Specialty</dt>
                    <dd className="truncate">{member.itSpecialtyName || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-500">Experience</dt>
                    <dd className="truncate">{member.totalExperienceInYears ?? '—'} Years</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-500">Availability</dt>
                    <dd className="truncate">{getAvailabilityLabel(member)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-slate-500">Organization</dt>
                    <dd className="truncate">{member.currentOrganization || member.currentLocation || '—'}</dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </div>

        {/* Desktop/tablet: table layout (md and up) */}
        <div className="hidden overflow-x-auto rounded-xl border border-gray-200 md:block">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[44px_72px_1.2fr_1fr_1fr_1fr_120px] gap-0 bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700">
              <span />
              <span>Photo</span>
              <span>Name</span>
              <span>IT Specialty</span>
              <span>Experience</span>
              <span>Availability</span>
              <span>Organization</span>
            </div>
            <div className="divide-y divide-gray-200 bg-white">
              {members.map((member) => {
                const selected = selectedIds.includes(member.userId);
                return (
                  <div key={member.userId} className="grid grid-cols-[44px_72px_1.2fr_1fr_1fr_1fr_120px] items-center gap-0 px-4 py-4 text-sm text-gray-700">
                    <button
                      type="button"
                      aria-label={`${selected ? 'Deselect' : 'Select'} ${buildMemberLabel(member)}`}
                      aria-pressed={selected}
                      onClick={() => handleToggleSelect(member.userId)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-gray-400 transition"                    >
                      {selected ? <Check className="h-4 w-4 text-blue-600" /> : null}
                    </button>
                    <div className="flex items-center justify-center">
                      <AvatarImage
                        profilePhotoId={Number(member.profilePhotoId ?? member.photoId) || null}
                        name={buildMemberLabel(member)}
                        alt={buildMemberLabel(member)}
                        size={40}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900">{buildMemberLabel(member)}</div>
                      <button
                        type="button"
                        onClick={() => handleViewProfile(member)}
                        className="mt-1 text-xs font-medium text-blue-600 hover:underline"
                      >
                        View Profile
                      </button>
                    </div>
                    <div className="truncate text-sm text-gray-600">{member.itSpecialtyName || '—'}</div>
                    <div className="truncate text-sm text-gray-600">{member.totalExperienceInYears ?? '—'} Years</div>
                    <div className="truncate text-sm text-gray-600">{getAvailabilityLabel(member)}</div>
                    <div className="truncate text-sm text-gray-600">{member.currentOrganization || member.currentLocation || '—'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <CreateGroupModal
        open={isGroupOpen}
        selectedUsers={selectedUsers as any}
        onClose={() => setIsGroupOpen(false)}
        onCreated={() => {
          setToast({ message: `${listLabel} created successfully.`, type: 'success' });
          setIsGroupOpen(false);
          setSelectedIds([]);
        }}
      />

      {showDeleteConfirm ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-900">Delete {listLabel}</h2>
            <p className="mt-2 text-sm text-gray-600">Are you sure you want to delete this {labels.privateList}?</p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="inline-flex h-11 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} /> : null}
    </div>
  );
}
